/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */
document.addEventListener("click", function(e) {
    const description = e.target.closest(".collection-header__description");

    if (description) {
      description.classList.toggle("collection-header__description--clamped");
      description.classList.toggle("collection-header__description--expanded");
    }
  });

  function initSubcollectionsCarousel(root, scrollerSel, prevSel, nextSel) {
    const scroller = root.querySelector(scrollerSel);
    const prevBtn = root.querySelector(prevSel);
    const nextBtn = root.querySelector(nextSel);

    if (!scroller || !prevBtn || !nextBtn) return;

    const getStep = () => {
      const firstItem = scroller.querySelector(".collection-subcollections__item");
      if (!firstItem) return Math.round(scroller.clientWidth * 0.8);
      const itemWidth = firstItem.getBoundingClientRect().width;
      return Math.max(120, Math.round(itemWidth + 16));
    };

    const updateButtons = () => {
      const maxScrollLeft = scroller.scrollWidth - scroller.clientWidth;
      const epsilon = 2;
      const atStart = scroller.scrollLeft <= epsilon;
      const atEnd = scroller.scrollLeft >= maxScrollLeft - epsilon;

      const isScrollable = maxScrollLeft > 1;
      root.classList.toggle("is-scrollable", isScrollable);
      scroller.classList.toggle("is-scrollable", isScrollable);
      prevBtn.hidden = !isScrollable || atStart;
      nextBtn.hidden = !isScrollable || atEnd;
    };

    prevBtn.addEventListener("click", () => {
      scroller.scrollBy({ left: -getStep(), behavior: "smooth" });
    });

    nextBtn.addEventListener("click", () => {
      scroller.scrollBy({ left: getStep(), behavior: "smooth" });
    });

    scroller.addEventListener(
      "focusin",
      (event) => {
        const link = event.target?.closest?.(".collection-subcollections__link");
        if (!link) return;
        link.scrollIntoView({ block: "nearest", inline: "nearest" });
      },
      true
    );

    scroller.addEventListener("scroll", updateButtons, { passive: true });
    window.addEventListener("resize", updateButtons);
    updateButtons();
  }

  function initSubcollectionsCarousels() {
    document.querySelectorAll("[data-subcollections-images-carousel]").forEach((root) => {
      initSubcollectionsCarousel(
        root,
        "[data-subcollections-images-scroller]",
        "[data-subcollections-images-prev]",
        "[data-subcollections-images-next]"
      );
    });
    document.querySelectorAll("[data-subcollections-icons-carousel]").forEach((root) => {
      initSubcollectionsCarousel(
        root,
        "[data-subcollections-icons-scroller]",
        "[data-subcollections-icons-prev]",
        "[data-subcollections-icons-next]"
      );
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initSubcollectionsCarousels);
  } else {
    initSubcollectionsCarousels();
  }

  // Keyboard a11y: keep focused link-bar items visible when tabbing
  document.addEventListener(
    "focusin",
    (event) => {
      const linkBarScroller = event.target?.closest?.("#collection-banner .link-bar__scroller");
      if (!linkBarScroller) return;

      const link = event.target?.closest?.("a, button");
      if (!link) return;

      // Ensure focused element is fully visible inside the horizontal scroller
      const scrollerRect = linkBarScroller.getBoundingClientRect();
      const linkRect = link.getBoundingClientRect();
      const padding = 12;

      const leftOverflow = linkRect.left - scrollerRect.left - padding;
      const rightOverflow = linkRect.right - scrollerRect.right + padding;

      if (leftOverflow < 0) {
        linkBarScroller.scrollBy({ left: leftOverflow, behavior: "smooth" });
      } else if (rightOverflow > 0) {
        linkBarScroller.scrollBy({ left: rightOverflow, behavior: "smooth" });
      }
    },
    true
  );

  var IPSTACK_KEY         = "607fd5882d54b0cc6b64d1bd27e36511";

  function GeoCaller(cb, onError)
  {
      var xhr = new XMLHttpRequest();
      xhr.onreadystatechange = function ()
      {
          if ( xhr.readyState !== 4 )
          {
              return;
          }

          if ( xhr.status === 200 )
          {
              try
              {
                  var result = JSON.parse(xhr.responseText);
                  console.log("Raw IPStack response:", result);
                  cb(result);
              }
              catch ( error )
              {
                  console.log("Geo lookup parse error:", error);
                  if ( onError )
                  {
                      onError(error);
                  }
              }
              return;
          }

          console.log("Geo lookup failed:", xhr.status);
          if ( onError )
          {
              onError(new Error("Geo lookup failed"));
          }
      };
      xhr.open("GET", "https://api.ipstack.com/check?access_key=" + IPSTACK_KEY, true);
      xhr.send();
  }
  
  function setCookie(cname, cvalue, exdays)
  {
      const d = new Date();
      d.setTime(d.getTime() + exdays * 24 * 60 * 60 * 1000);
      let expires = "expires=" + d.toUTCString();
      document.cookie = cname + "=" + cvalue + "; " + expires + "; path=/; domain=arneclo.com";
  }
  
  function GetCookie(cname)
  {
      let name = cname + "=";
      let ca = document.cookie.split(";");
      for (let i = 0; i < ca.length; i++)
      {
          let c = ca[i];
          while (c.charAt(0) == " ")
          {
              c = c.substring(1);
          }
          if (c.indexOf(name) == 0)
          {
              return c.substring(name.length, c.length);
          }
      }
      return "";
  }
  
  function updateCurrencyDisplay(countryCode) {
      let currencyText = "";
  
      switch (countryCode.toUpperCase()) {
          case "IE": // Ireland
              currencyText = "€30";
              break;
          case "US": // United States
              currencyText = "$30";
              break;
          case "NL": // Netherlands
              currencyText = "€30";
              break;
          default:
              return; // Do nothing if the country is not listed
      }
  
      // Update all elements with the class "lion-currency__value"
      document.querySelectorAll(".lion-currency__value").forEach(element => {
          element.textContent = currencyText;
      });
  }
  
  
  function GetParam( param )
  {
      return new URLSearchParams( window.location.search ).get( param );
  }

  function getGeoPopupDismissedCookieName( siteCountry )
  {
      return "arne_geo_popup_dismissed_" + ( siteCountry || "" ).toUpperCase();
  }

  function isGeoPopupDismissed( siteCountry )
  {
      return GetCookie( getGeoPopupDismissedCookieName( siteCountry ) ) === "1";
  }

  function setGeoPopupDismissed( siteCountry )
  {
      setCookie( getGeoPopupDismissedCookieName( siteCountry ), "1", 0.25 );
  }
  
  
  
  function LoadGeoSection( cb )
  {
      var geoContainer = document.querySelector( ".recommendation-modal__backdrop_new" );
      if ( geoContainer )
      {
          console.log( "geo content already loaded :", geoContainer );
          cb( );
      }
      else
      {
          console.log( "loading geo content" );
          if ( window._geoSectionLoadingQ )
          {
              window._geoSectionLoadingQ.push( cb );
          }
          else
          {
              window._geoSectionLoadingQ = [ cb ];
  
              var xhr = new XMLHttpRequest();
              xhr.onreadystatechange = function () 
              {
                  if ( ( xhr.readyState == 4 ) && ( xhr.status == 200 ) ) 
                  {
                      var result = JSON.parse(xhr.responseText);
                      var markup = result[ "arne-locator-popup-content" ];
                      document.body.insertAdjacentHTML( "beforeend", markup );
                      prepareGeoSection();
                      handlePopup();
  
                      // emergency patch
                      try 
                      {
                          var elements = document.querySelectorAll( "#shopify-section-arne-locator-popup-content form[action='/localization'] input[name='return_to']");
                          elements.forEach( function( element )
                          {
                              element.setAttribute( "value", "/" );
                              element.value = "/";
                          } );                      
                      } 
                      catch (error) 
                      {
                        console.log( "ERROR ", error.stack );  
                      }
                    
                      var q = window._geoSectionLoadingQ;
                      window._geoSectionLoadingQ = undefined;
                      q.forEach( function( qcb )
                      {
                          qcb( );
                      } );
                  }
              };
              xhr.open( "GET", window.Shopify.routes.root + "?sections=arne-locator-popup-content", true );
              xhr.send( );
          }        
      }
  }
  
  function prepareGeoSection()
  {
      var backdrop       = document.querySelector( ".recommendation-modal__backdrop_new" );
      var mainPopup      = document.querySelector( ".locator-popup-main" );
      var locationsPopup = document.querySelector( ".locater-popup-locations" );

      if ( backdrop )
      {
          backdrop.style.display = "none";
      }
      if ( mainPopup )
      {
          mainPopup.style.display = "none";
      }
      if ( locationsPopup )
      {
          locationsPopup.style.display = "none";
      }

      document.querySelectorAll( ".locator-popup-country-content" ).forEach( function( content )
      {
          content.classList.add( "hidden" );
          content.setAttribute( "hidden", "" );
          content.setAttribute( "aria-hidden", "true" );
      } );
  }

  function updateGeolocationContent( countryCode )
  {
      countryCode = ( countryCode || "" ).toLowerCase();
      document.querySelectorAll( ".locator-popup-country-content" ).forEach( function( content )
      {
          var isTarget = content.dataset.countryCode === countryCode;
          if ( isTarget )
          {
              content.classList.remove( "hidden" );
              content.removeAttribute( "hidden" );
              content.setAttribute( "aria-hidden", "false" );
          }
          else
          {
              content.classList.add( "hidden" );
              content.setAttribute( "hidden", "" );
              content.setAttribute( "aria-hidden", "true" );
          }
      } );
  }

  function ShowGeolocationContent( response )
  {
      LoadGeoSection( function( )
      {
          updateGeolocationContent( response.country_code );
      } );
  }

  function setDataOnBody( code )
  {
      var body = document.querySelector( "body" );
      body.setAttribute( "data-market", code.toLowerCase() );
  }

  function getSiteCountry()
  {
      var siteCountry = window.themeVariables && window.themeVariables.settings && window.themeVariables.settings.siteCountry;
      if ( !siteCountry && window.Shopify && window.Shopify.country )
      {
          siteCountry = window.Shopify.country;
      }
      return ( siteCountry || "" ).toUpperCase();
  }

  function shouldShowGeoPopup( siteCountry, detectedCountry )
  {
      siteCountry     = ( siteCountry || "" ).toUpperCase();
      detectedCountry = ( detectedCountry || "" ).toUpperCase();
      if ( !siteCountry || !detectedCountry )
      {
          return false;
      }
      if ( siteCountry === "GB" && detectedCountry !== "GB" )
      {
          return true;
      }
      if ( siteCountry !== "GB" && detectedCountry === "GB" )
      {
          return true;
      }
      return false;
  }

  function getPopupTargetCountry( siteCountry, detectedCountry )
  {
      siteCountry     = ( siteCountry || "" ).toUpperCase();
      detectedCountry = ( detectedCountry || "" ).toUpperCase();
      if ( siteCountry !== "GB" && detectedCountry === "GB" )
      {
          return "GB";
      }
      return detectedCountry;
  }

  function isGeoPopupVisible( backdrop )
  {
      return !!( backdrop && getComputedStyle( backdrop ).display !== "none" );
  }

  function focusGeoPopupElement( element )
  {
      if ( !element || typeof element.focus !== "function" )
      {
          return;
      }

      try
      {
          element.focus( { preventScroll: true } );
      }
      catch ( error )
      {
          element.focus();
      }
  }

  function getGeoPopupElements()
  {
      return {
          backdrop: document.querySelector( ".recommendation-modal__backdrop_new" ),
          container: document.querySelector( ".locater-popup" ),
          closeButton: document.querySelector( ".locater-popup-close" )
      };
  }

  function getGeoPopupFocusableElements( container )
  {
      if ( !container )
      {
          return [];
      }
      return Array.prototype.slice.call(
          container.querySelectorAll( "a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex='-1'])" )
      ).filter( function( el )
      {
          return el.offsetParent !== null;
      } );
  }

  function deactivateGeoPopupAccessibility()
  {
      if ( window._geoPopupFocusTrapHandler )
      {
          document.removeEventListener( "keydown", window._geoPopupFocusTrapHandler );
          window._geoPopupFocusTrapHandler = null;
      }

      var popupEls = getGeoPopupElements();
      if ( popupEls.backdrop )
      {
          popupEls.backdrop.setAttribute( "aria-hidden", "true" );
      }

      if ( window._geoPopupPreviousFocus && document.contains( window._geoPopupPreviousFocus ) )
      {
          window._geoPopupPreviousFocus.focus();
      }
      window._geoPopupPreviousFocus = null;
  }

  function activateGeoPopupAccessibility()
  {
      var popupEls = getGeoPopupElements();
      var container = popupEls.container;

      if ( !container )
      {
          return;
      }

      if ( document.activeElement && !container.contains( document.activeElement ) )
      {
          window._geoPopupPreviousFocus = document.activeElement;
      }

      if ( popupEls.backdrop )
      {
          popupEls.backdrop.setAttribute( "aria-hidden", "false" );
      }

      container.setAttribute( "role", "dialog" );
      container.setAttribute( "aria-modal", "true" );
      container.setAttribute( "tabindex", "-1" );

      var focusable = getGeoPopupFocusableElements( container );
      if ( popupEls.closeButton )
      {
          popupEls.closeButton.setAttribute( "tabindex", "0" );
          focusGeoPopupElement( popupEls.closeButton );
      }
      else if ( focusable.length )
      {
          focusGeoPopupElement( focusable[0] );
      }
      else
      {
          focusGeoPopupElement( container );
      }

      if ( window._geoPopupFocusTrapHandler )
      {
          document.removeEventListener( "keydown", window._geoPopupFocusTrapHandler );
      }

      window._geoPopupFocusTrapHandler = function( event )
      {
          if ( !isGeoPopupVisible( popupEls.backdrop ) )
          {
              return;
          }

          if ( event.key === "Escape" )
          {
              if ( popupEls.closeButton )
              {
                  popupEls.closeButton.click();
              }
              return;
          }

          if ( event.key !== "Tab" )
          {
              return;
          }

          var modalFocusable = getGeoPopupFocusableElements( container );
          if ( !modalFocusable.length )
          {
              event.preventDefault();
              return;
          }

          var first = modalFocusable[0];
          var last = modalFocusable[ modalFocusable.length - 1 ];
          var isShift = event.shiftKey;

          if ( isShift && document.activeElement === first )
          {
              event.preventDefault();
              last.focus();
          }
          else if ( !isShift && document.activeElement === last )
          {
              event.preventDefault();
              first.focus();
          }
      };

      document.addEventListener( "keydown", window._geoPopupFocusTrapHandler );
  }

  function showGeoPopup( targetCountryCode )
  {
      LoadGeoSection( function( )
      {
          targetCountryCode = ( targetCountryCode || "" ).toLowerCase();
          updateGeolocationContent( targetCountryCode );
          HandleGeolocationPopupForm( targetCountryCode );

          var backdrop       = document.querySelector( ".recommendation-modal__backdrop_new" );
          var locationsPopup = document.querySelector( ".locater-popup-locations" );
          var mainPopup      = document.querySelector( ".locator-popup-main" );

          if ( mainPopup )
          {
              mainPopup.style.display = "block";
          }
          if ( locationsPopup )
          {
              locationsPopup.style.display = "flex";
          }
          if ( backdrop )
          {
              backdrop.style.display = "flex";
          }

          activateGeoPopupAccessibility();
      } );
  }

  function handleDetectedCountry( detectedCountry, siteCountry )
  {
      detectedCountry = ( detectedCountry || "" ).toUpperCase();
      siteCountry     = siteCountry || getSiteCountry();

      setDataOnBody( detectedCountry );
      updateCurrencyDisplay( detectedCountry );

      if ( shouldShowGeoPopup( siteCountry, detectedCountry ) && !isGeoPopupDismissed( siteCountry ) )
      {
          showGeoPopup( getPopupTargetCountry( siteCountry, detectedCountry ) );
      }
  }

  function HandleArneGeolocation( )
  {
      var forceCountry      = GetParam( "forcecountry" );
      var cachedCountryCode = GetCookie( "arne_geo_country_code" );
      var siteCountry       = getSiteCountry();

      if ( forceCountry )
      {
          setCookie( "arne_geo_country_code", forceCountry, 0.25 );
          handleDetectedCountry( forceCountry, siteCountry );
          return;
      }

      if ( cachedCountryCode )
      {
          handleDetectedCountry( cachedCountryCode, siteCountry );
          return;
      }

      GeoCaller( function( response )
      {
          setCookie( "arne_geo_country_code", response.country_code, 0.25 );
          handleDetectedCountry( response.country_code, siteCountry );
      } );
  }
  
  function HandleGeolocationPopupForm( countryCode ) 
  {
      console.log("HandleGeolocationPopupForm Country Code:", countryCode);
      var dropdown   = document.querySelector('.locator-popup-dropdown');
      var form       = document.querySelector('.localization-form');
  
      if ( dropdown && form ) 
      {
          var options = dropdown.options;
          if ( !dropdown.dataset.initialized ) 
          {
              for ( var i = 0; i < options.length; i++ ) 
              {
                  if (options[i].value.toLowerCase() === countryCode.toLowerCase()) 
                  {
                      dropdown.selectedIndex = i;
                      break;
                  }
              }
              dropdown.dataset.initialized = true;
          }
  
          var continueBtn = document.querySelector('.locator-first-btn');
          if (continueBtn) 
          {
              continueBtn.addEventListener( 'click', function (e) 
              {
                  e.preventDefault();
                  form.submit();
              });
          }
  
          dropdown.addEventListener('change', function () 
          {
              var selectedCountryCode = dropdown.options[dropdown.selectedIndex].value.toLowerCase();
              updateGeolocationContent(selectedCountryCode);
          });
      }
  }
  
  
  function initArneGeolocation()
  {
      HandleArneGeolocation();
  }

  if ( document.readyState === "loading" )
  {
      document.addEventListener( "DOMContentLoaded", initArneGeolocation );
  }
  else
  {
      initArneGeolocation();
  }
  
  function handlePopupShow( command, mainPopup, locationsPopup ) 
  {
      var mainPopupDisplay         = "none";
      var locationsPopupDisplay    = "flex";
  
      if ( command === "show" ) 
      {
          mainPopupDisplay         = "block";
          locationsPopupDisplay    = "none";
      }
  
      mainPopup.style.display      = mainPopupDisplay;
      locationsPopup.style.display = locationsPopupDisplay;
  }
  
  function handlePopup() {
    var closePopupBtn           = document.querySelector('.locater-popup-close');
    var changeLocationBtns      = document.querySelectorAll('.locater-popup-change-location');
    var cancelLocationChangeBtn = document.querySelector('.locater-popup-cancel');
    var backdrop                = document.querySelector('.recommendation-modal__backdrop_new');
    var mainPopup               = document.querySelector('.locator-popup-main');
    var locationsPopup          = document.querySelector('.locater-popup-locations');

    if ( !backdrop || backdrop.dataset.popupBound === "true" )
    {
      return;
    }
    backdrop.dataset.popupBound = "true";
  
    if (closePopupBtn) {
      console.log("closePopupBtn found");
      closePopupBtn.setAttribute('role', 'button');
      closePopupBtn.setAttribute('aria-label', 'Close location popup');
      closePopupBtn.addEventListener('click', function () {
        console.log("Close button clicked");
        setGeoPopupDismissed( getSiteCountry() );
        backdrop.style.display = 'none';
        locationsPopup.style.display = 'none';
        if ( mainPopup )
        {
            mainPopup.style.display = 'none';
        }
        deactivateGeoPopupAccessibility();
      });
      closePopupBtn.addEventListener('keydown', function (event) {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          closePopupBtn.click();
        }
      });
    } else {
      console.warn("closePopupBtn NOT found at time of binding");
    }
  
    if (changeLocationBtns && changeLocationBtns.length) {
      changeLocationBtns.forEach(function (btn) {
        btn.addEventListener('click', function () {
          console.log("Change location clicked");
          handlePopupShow("hide", mainPopup, locationsPopup);
        });
      });
    }
  
    if (cancelLocationChangeBtn) {
      cancelLocationChangeBtn.addEventListener('click', function () {
        console.log("Cancel clicked");
        handlePopupShow("show", mainPopup, locationsPopup);
      });
    }
  }
  
  document.addEventListener('DOMContentLoaded', function () 
  {
      handlePopup();
  });

  // Contact Sidebar Toggle Handler
  document.addEventListener('click', function(e) {
    if (e.target.closest('.contact_sidebar_toggle')) {
      const contactSidebar = document.querySelector('.contact_sidebar');
      const body = document.body;

      if (contactSidebar) {
        contactSidebar.classList.toggle('opened');
      }

      if (!body.classList.contains('c_sidebar_opened')) {
        body.classList.add('c_sidebar_opened');
      }

      if (body.classList.contains('menu_opened')) {
        body.classList.remove('menu_opened');
      }
    }

    // Close contact sidebar when clicking overlay
    if (e.target.classList.contains('custom_sidebar_overlay')) {
      const contactSidebar = document.querySelector('.contact_sidebar');
      const body = document.body;

      if (contactSidebar) {
        contactSidebar.classList.remove('opened');
      }

      body.classList.remove('c_sidebar_opened');
    }

    // Close button handler
    if (e.target.closest('.sidebar_closer')) {
      const contactSidebar = document.querySelector('.contact_sidebar');
      const body = document.body;

      if (contactSidebar) {
        contactSidebar.classList.remove('opened');
      }

      body.classList.remove('c_sidebar_opened');
    }
  });
   
  document.addEventListener("click", (event) => {
    const proxy = event.target.closest("[data-proxy-toggle]");
    if (!proxy) return;

    const wrapper = proxy.closest(".shop-the-look__product-wrapper");
    if (!wrapper) return;

    const quickViewLink = wrapper.querySelector(".shop-the-look__product-link");
    if (!quickViewLink) return;

    quickViewLink.click();
  });
  
  document.addEventListener("shop-the-look:slide-changed", (event) => {
    const shopTheLook = event.target; 
    const activeIndex = shopTheLook.selectedIndex;
    const dots = shopTheLook.querySelectorAll(".shop-the-look__dot-nav");
  
    dots.forEach((dot, index) => {
      if (index === activeIndex) {
        dot.classList.add("is-active");
      } else {
        dot.classList.remove("is-active");
      }
    });
  });
  
  
(function initPinchToZoom() {
  const initialized = new WeakSet();

  function attachPinchHandler(mediaElement) {
    if (initialized.has(mediaElement)) return;
    initialized.add(mediaElement);

    const carousel = mediaElement.querySelector('flickity-carousel');
    if (!carousel) return;

    let scale = 1;
    let offsetX = 0;
    let offsetY = 0;
    let lastDist = null;
    let lastSingleX = null;
    let lastSingleY = null;
    let activeImg = null;
    let activeWrapper = null;
    let isZoomed = false;

    function getSelectedImage() {
      const item = mediaElement.querySelector('.product__media-item.is-selected');
      if (!item || item.getAttribute('data-media-type') !== 'image') return null;
      return item.querySelector('img');
    }

    function getSelectedWrapper() {
      const item = mediaElement.querySelector('.product__media-item.is-selected');
      return item ? item.querySelector('.product__media-image-wrapper') : null;
    }

    function clamp(val, min, max) {
      return Math.min(max, Math.max(min, val));
    }

    function clampOffsets() {
      if (!activeWrapper) return;
      const w = activeWrapper.clientWidth;
      const h = activeWrapper.clientHeight;
      offsetX = clamp(offsetX, w * (1 - scale), 0);
      offsetY = clamp(offsetY, h * (1 - scale), 0);
    }

    function applyTransform() {
      if (!activeImg) return;
      activeImg.style.transformOrigin = '0 0';
      activeImg.style.transform = `translate(${offsetX}px, ${offsetY}px) scale(${scale})`;
    }

    function resetZoom(animate) {
      scale = 1; offsetX = 0; offsetY = 0;
      isZoomed = false;
      lastDist = null; lastSingleX = null; lastSingleY = null;
      if (activeImg) {
        if (animate) {
          activeImg.style.transition = 'transform 0.2s ease';
          activeImg.style.transform = '';
          activeImg.style.transformOrigin = '';
          setTimeout(() => { if (activeImg) activeImg.style.transition = ''; }, 220);
        } else {
          activeImg.style.transition = '';
          activeImg.style.transform = '';
          activeImg.style.transformOrigin = '';
        }
        activeImg.style.willChange = '';
      }
      if (activeWrapper) activeWrapper.style.touchAction = '';
      carousel.setDraggable(true);
      activeImg = null;
      activeWrapper = null;
    }

    carousel.addEventListener('touchstart', function (e) {
      if (e.touches.length === 2) {
        const img = getSelectedImage();
        if (!img) return;

        e.stopPropagation();

        if (!activeImg) {
          activeImg = img;
          activeWrapper = getSelectedWrapper();
          activeImg.style.willChange = 'transform';
          if (activeWrapper) activeWrapper.style.touchAction = 'none';
          carousel.setDraggable(false);
        }

        const t1 = e.touches[0], t2 = e.touches[1];
        lastDist = Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);
        lastSingleX = null; lastSingleY = null;

      } else if (e.touches.length === 1 && isZoomed) {
        e.stopPropagation();
        lastSingleX = e.touches[0].clientX;
        lastSingleY = e.touches[0].clientY;
      }
    }, { passive: false });

    carousel.addEventListener('touchmove', function (e) {
      if (e.touches.length === 2 && activeImg) {
        e.preventDefault();
        const t1 = e.touches[0], t2 = e.touches[1];
        const newDist = Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);
        const rect = carousel.getBoundingClientRect();
        const midX = ((t1.clientX + t2.clientX) / 2) - rect.left;
        const midY = ((t1.clientY + t2.clientY) / 2) - rect.top;

        const ratio = newDist / lastDist;
        const newScale = clamp(scale * ratio, 1, 4);

        const imgSpaceX = (midX - offsetX) / scale;
        const imgSpaceY = (midY - offsetY) / scale;
        offsetX = midX - imgSpaceX * newScale;
        offsetY = midY - imgSpaceY * newScale;
        scale = newScale;

        clampOffsets();
        applyTransform();
        lastDist = newDist;
        lastSingleX = null; lastSingleY = null;
        isZoomed = scale > 1.01;

      } else if (e.touches.length === 1 && isZoomed && activeImg) {
        e.preventDefault();
        const curX = e.touches[0].clientX;
        const curY = e.touches[0].clientY;
        if (lastSingleX !== null) {
          offsetX += curX - lastSingleX;
          offsetY += curY - lastSingleY;
          clampOffsets();
          applyTransform();
        }
        lastSingleX = curX;
        lastSingleY = curY;
      }
    }, { passive: false });

    carousel.addEventListener('touchend', function (e) {
      if (e.touches.length < 2) lastDist = null;
      if (e.touches.length === 0) {
        lastSingleX = null; lastSingleY = null;
        if (scale <= 1.05) {
          resetZoom(true);
        }
      }
    }, { passive: true });

    carousel.addEventListener('flickity:slide-changed', function () {
      if (isZoomed || activeImg) resetZoom(false);
    });
  }

  function init() {
    document.querySelectorAll('product-media').forEach(attachPinchHandler);

    new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType !== Node.ELEMENT_NODE) return;
          if (node.matches('product-media')) {
            attachPinchHandler(node);
          }
          node.querySelectorAll('product-media').forEach(attachPinchHandler);
        });
      });
    }).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

(function initFullStoryAddToCartEvent() {
  document.addEventListener(
    'variant:added',
    function (event) {
      if (typeof FS !== 'function') return;

      const line = event.detail && event.detail.variant;
      if (!line || typeof line !== 'object') return;

      const productId = line.product_id;
      const variantId = line.variant_id != null ? line.variant_id : null;
      const quantity = typeof line.quantity === 'number' ? line.quantity : 1;
      const sku = typeof line.sku === 'string' ? line.sku : '';

      const props = {};
      if (productId != null) props.product_id = productId;
      if (variantId != null) props.variant_id = variantId;
      props.quantity = quantity;
      if (sku) props.sku = sku;

      const unitCents =
        typeof line.final_price === 'number'
          ? line.final_price
          : typeof line.price === 'number'
            ? line.price
            : undefined;
      if (typeof unitCents === 'number' && !Number.isNaN(unitCents)) {
        props.price_cents = unitCents;
      }

      try {
        FS('trackEvent', { name: 'add_to_cart', properties: props });
      } catch (_) {}
    },
    false
  );
})();

(function initFullStoryNavDrawerEvents() {
  document.addEventListener(
    'nav-drawer:open',
    function () {
      if (typeof FS !== 'function') return;
      try {
        FS('trackEvent', { name: 'nav_opened', properties: {} });
      } catch (_) {}
    },
    false
  );

  document.addEventListener(
    'nav-drawer:close',
    function (event) {
      if (typeof FS !== 'function') return;

      const method =
        event.detail && typeof event.detail.method === 'string' ? event.detail.method : 'unknown';

      try {
        FS('trackEvent', { name: 'nav_closed', properties: { method: method } });
      } catch (_) {}
    },
    false
  );

  document.addEventListener(
    'nav-drawer:item-click',
    function (event) {
      if (typeof FS !== 'function') return;

      const detail = event.detail || {};
      const props = {};
      if (detail.tier) props.tier = detail.tier;
      if (detail.label) props.label = detail.label;

      try {
        FS('trackEvent', { name: 'nav_item_clicked', properties: props });
      } catch (_) {}
    },
    false
  );
})();

(function initCollectionCanonicalSync() {
  // The theme's facet/pagination component re-renders the product grid over fetch and
  // updates the URL bar via history.replaceState (see "pagination:page-changed" and
  // "facet:criteria-changed" in theme.js), without a full page load. That means <head>
  // (canonical link, robots meta) is never refreshed by the theme itself. This mirrors
  // the canonical/robots logic from layout/theme.liquid so the DOM stays consistent with
  // the URL after an in-place pagination or filter/sort change.
  function computeCanonicalHref(urlString) {
    var url = new URL(urlString, window.location.origin);
    var hasActiveFilters = false;
    var hasSort = false;

    url.searchParams.forEach(function (value, key) {
      if (key.indexOf('filter.') === 0 && value !== '') {
        hasActiveFilters = true;
      }
      if (key === 'sort_by' && value !== '') {
        hasSort = true;
      }
    });

    var base = url.origin + url.pathname;
    if (hasActiveFilters || hasSort) {
      return base;
    }

    var page = parseInt(url.searchParams.get('page') || '1', 10);
    return page > 1 ? base + '?page=' + page : base;
  }

  function updateRobotsMeta(urlString) {
    var url = new URL(urlString, window.location.origin);
    var page = parseInt(url.searchParams.get('page') || '1', 10);
    var meta = document.querySelector('meta[name="robots"][data-pagination-robots]');

    if (page > 1) {
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('name', 'robots');
        meta.setAttribute('data-pagination-robots', '');
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', 'index, follow');
    } else if (meta) {
      meta.remove();
    }
  }

  // PagePagination's own click handler (theme.js) ignores the link's href entirely: it
  // rebuilds the URL from the current page plus the clicked link's data-page attribute,
  // always force-setting ?page=N (including N=1), then pushes that straight to the address
  // bar via history.replaceState. That happens synchronously, on the same event, before this
  // listener runs (ProductFacet's own listener sits between the dispatching element and
  // document in the bubble chain, so it always fires first) — so we correct the address bar
  // immediately afterwards rather than trying to prevent the vendor's replaceState call.
  function stripRedundantPageOne(urlString) {
    var url = new URL(urlString, window.location.origin);
    if ((url.searchParams.get('page') || '1') === '1') {
      url.searchParams.delete('page');
    }
    return url.toString();
  }

  function handleUrlChanged(event) {
    if (window.location.pathname.indexOf('/collections/') !== 0) return;
    var url = event.detail && event.detail.url;
    if (!url) return;

    var canonicalLink = document.querySelector('link[rel="canonical"]');
    if (canonicalLink) {
      canonicalLink.setAttribute('href', computeCanonicalHref(url));
    }
    updateRobotsMeta(url);
    history.replaceState({}, '', stripRedundantPageOne(url));
  }

  document.addEventListener('pagination:page-changed', handleUrlChanged);
  document.addEventListener('facet:criteria-changed', handleUrlChanged);
})();

(function initProductMediaCounter() {
  const initialized = new WeakSet();

  function updateCounter(productMedia) {
    const counter = productMedia.querySelector(':scope > .product__media-list-wrapper > .product__media-counter');
    if (!counter) return;
    const text = counter.querySelector('span');
    if (!text) return;
    const list = productMedia.querySelector('.product__media-list');
    if (!list) return;
    const cells = Array.from(list.querySelectorAll('.product__media-item:not(.is-filtered)'));
    const selected = list.querySelector('.product__media-item.is-selected:not(.is-filtered)');
    if (cells.length < 2 || !selected) {
      counter.hidden = true;
      return;
    }
    const index = cells.indexOf(selected) + 1;
    if (index < 1) {
      counter.hidden = true;
      return;
    }
    counter.hidden = false;
    text.textContent = `(${index}/${cells.length})`;
  }

  function attach(productMedia) {
    if (initialized.has(productMedia)) return;
    initialized.add(productMedia);
    const carousel = productMedia.querySelector('flickity-carousel');
    if (!carousel) return;
    const update = () => updateCounter(productMedia);
    carousel.addEventListener('flickity:ready', update);
    carousel.addEventListener('flickity:slide-changed', update);
    update();
  }

  function init() {
    document.querySelectorAll('product-media').forEach(attach);

    new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType !== Node.ELEMENT_NODE) return;
          if (node.matches('product-media')) attach(node);
          node.querySelectorAll('product-media').forEach(attach);
        });
      });
    }).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

(() => {
  const FADE_MS = 200;
  const instances = [];
  let lastScroll = window.scrollY;

  const createInstance = ({ selector, spacerClass = 'sticky-scroll-up-spacer', shouldHold = () => false }) => {
    let element = null;
    let spacer = null;
    let initialTop = 0;
    let hideTimer = null;

    const ensureSpacer = () => {
      if (spacer && spacer.isConnected) return spacer;
      const sibling = element.previousElementSibling;
      if (sibling && sibling.classList.contains(spacerClass)) {
        spacer = sibling;
      } else {
        spacer = document.createElement('div');
        spacer.className = spacerClass;
        spacer.setAttribute('aria-hidden', 'true');
        spacer.style.height = '0px';
        element.insertAdjacentElement('beforebegin', spacer);
      }
      return spacer;
    };

    const refresh = () => {
      element = document.querySelector(selector);
      if (!element) return;
      if (!element.classList.contains('is-sticky')) {
        initialTop = element.offsetTop;
      }
      ensureSpacer();
    };

    const resetSpacer = () => {
      if (!spacer) return;
      spacer.style.height = '0px';
      spacer.style.marginTop = '0px';
      spacer.style.marginBottom = '0px';
    };

    const setSticky = (sticky) => {
      if (sticky) {
        clearTimeout(hideTimer);
        hideTimer = null;

        if (!element.classList.contains('is-sticky')) {
          const style = getComputedStyle(element);
          const height = element.getBoundingClientRect().height;
          const marginTop = style.marginTop;
          const marginBottom = style.marginBottom;
          element.classList.add('is-sticky');
          if (getComputedStyle(element).position === 'fixed' && ensureSpacer()) {
            spacer.style.height = height + 'px';
            spacer.style.marginTop = marginTop;
            spacer.style.marginBottom = marginBottom;
          }
          element.offsetHeight;
        }
        element.classList.add('is-visible');
        return;
      }

      if (!element.classList.contains('is-sticky') || hideTimer) return;

      element.classList.remove('is-visible');
      hideTimer = setTimeout(() => {
        hideTimer = null;
        element.classList.remove('is-sticky');
        resetSpacer();
      }, FADE_MS);
    };

    const release = () => {
      clearTimeout(hideTimer);
      hideTimer = null;
      if (!element.classList.contains('is-sticky')) return;
      element.classList.remove('is-sticky', 'is-visible');
      resetSpacer();
    };

    const update = (currentScroll, scrollingUp, top) => {
      if (!element || !element.isConnected) {
        refresh();
        if (!element) return 0;
      }

      element.style.setProperty('--sticky-scroll-up-top', `${top}px`);

      if (!shouldHold(element)) {
        if (!element.classList.contains('is-sticky')) initialTop = element.offsetTop;

        if (currentScroll <= initialTop - top) {
          release();
        } else {
          setSticky(scrollingUp);
        }
      }

      const isPinned = element.classList.contains('is-sticky') && getComputedStyle(element).position === 'fixed';
      return isPinned ? element.offsetHeight : 0;
    };

    refresh();
    return { refresh, update };
  };

  const onScroll = () => {
    if (document.body.classList.contains('overflow-hidden')) return;

    const currentScroll = window.scrollY;
    if (currentScroll === lastScroll) return;

    const scrollingUp = currentScroll < lastScroll;
    let top = 0;
    instances.forEach((instance) => {
      top += instance.update(currentScroll, scrollingUp, top);
    });

    lastScroll = currentScroll;
  };

  window.addEventListener('scroll', onScroll, { passive: true });

  window.StickyScrollUp = {
    register(options) {
      const instance = createInstance(options);
      instances.push(instance);
      return { refresh: instance.refresh };
    },
  };

  if (document.querySelector('store-header[sticky]')) {
    window.StickyScrollUp.register({ selector: '.shopify-section--header' });
  }
})();
